import { OrderLimitType } from '@/constants/enum';
import { shippingSettingSelector } from '@/redux/slice/shipping.slice';
import { ITimeSlot } from '@/types';
import { time24hTo12h } from '@/utils';
import { BlockStack, Button, InlineError, InlineGrid, InlineStack, Select, TextField } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const TimeSlot = ({
  index,
  time24h,
  timeSlot,
  listTimeSlots,
  setTimeSlot,
  selectedDay,
  onDeleteTimeSlot,
}: {
  index: number;
  time24h: { id: number; value: string }[];
  timeSlot: ITimeSlot;
  listTimeSlots: ITimeSlot[];
  setTimeSlot: (timeSlot: ITimeSlot) => void;
  selectedDay: number;
  onDeleteTimeSlot: () => void;
}) => {
  const { t } = useTranslation('shippingSettings');
  const shippingSettings = useSelector(shippingSettingSelector);

  const timeStarts = useMemo(() => {
    return time24h.map((item) => ({
      key: item.value,
      label: time24hTo12h(item.value),
      value: item.value,
    }));
  }, [time24h]);

  const timeEnds = useMemo(() => {
    const startIndex = time24h.findIndex((item) => item.value === timeSlot.start);
    return time24h.slice(startIndex).map((item) => ({
      key: item.value,
      label: time24hTo12h(item.value),
      value: item.value,
    }));
  }, [time24h, timeSlot.start]);

  return (
    <BlockStack gap="150">
      <InlineGrid
        columns={shippingSettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT ? '1fr 1fr 1fr auto' : '1fr 1fr auto'}
        gap="300"
      >
        <Select
          labelHidden={index !== 0}
          label={t('shippingTime.timeSlot.from.label')}
          placeholder={t('shippingTime.timeSlot.from.placeholder')}
          options={timeStarts}
          value={timeSlot.start}
          onChange={(value) => setTimeSlot({ ...timeSlot, start: value })}
        />
        <Select
          labelHidden={index !== 0}
          label={t('shippingTime.timeSlot.until.label')}
          placeholder={t('shippingTime.timeSlot.until.placeholder')}
          options={timeEnds}
          value={timeSlot.end}
          onChange={(value) => setTimeSlot({ ...timeSlot, end: value })}
        />
        {shippingSettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT && (
          <TextField
            label={t('orderLimit.title')}
            labelHidden={index !== 0}
            placeholder={t('orderLimit.title')}
            type="number"
            value={timeSlot?.orderLimit?.toString()}
            onChange={(value) => setTimeSlot({ ...timeSlot, orderLimit: Number(value) })}
            autoComplete="off"
            min={0}
            step={1}
          />
        )}
        <InlineStack blockAlign="end">
          <Button
            icon={DeleteIcon}
            onClick={onDeleteTimeSlot}
            size="large"
            disabled={listTimeSlots.length <= 1}
            tone="critical"
          />
        </InlineStack>
      </InlineGrid>
      {!!timeSlot.error && <InlineError message={timeSlot.error} fieldID={`timeSlot-${selectedDay}-${index}`} />}
    </BlockStack>
  );
};

export default TimeSlot;
